import React from 'react';
import { Users, Target, Heart, Globe, Award, Leaf } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import MorphingGradient from '../components/MorphingGradient';
import AmbientBackground from '../components/AmbientBackground';
import AnimatedIcon from '../components/AnimatedIcon';
import { motion } from 'framer-motion';

const About = () => {
  const { t } = useLanguage();
  const team = [
    {
      name: "Tareq Tua'ayman",
      role: 'CEO & AI Engineer',
      description: "Leading the vision and technical development of Yamai's AI systems.",
    },
    {
      name: 'Dr. Ahmed Al-Asali',
      role: 'Chief Medical Officer',
      description: 'Ensuring clinical validity and medical accuracy across all projects.',
    },
  ];

  const futureTeams = [
    {
      name: 'R&D',
      icon: Target,
      description: 'Developing cutting-edge AI algorithms and models',
    },
    {
      name: 'Clinical Validation',
      icon: Heart,
      description: 'Testing and validating systems with medical professionals',
    },
    {
      name: 'Product Engineering',
      icon: Globe,
      description: 'Building scalable, reliable diagnostic platforms',
    },
    {
      name: 'Partnerships',
      icon: Users,
      description: 'Collaborating with hospitals and research institutions',
    },
  ];

  return (
    <div className="min-h-screen relative pt-32 pb-24">
      <SEO 
        title={t('about.title')}
        description="Yamai - Building explainable medical AI systems. Founded in 2025 in Breda, Netherlands. Our mission is to empower physicians with transparent AI decision support."
        keywords="Yamai, medical AI company, explainable AI, healthcare innovation, AI research, Netherlands, Breda"
      />

      {/* Professional Background */}
      <MorphingGradient />
      <AmbientBackground />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient-animate">
              {t('about.title')}
            </span>
          </h1>
          <p className="text-[#8A90A0] text-xl max-w-3xl mx-auto leading-relaxed">
            {t('home.foundation.subtitle')}
          </p>
        </motion.div>

        {/* Mission, Vision, Values */}
        <div className="grid md:grid-cols-3 gap-8 mb-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="glass-effect rounded-2xl p-8 border border-yamai-purple-500/30 hover:border-yamai-purple-500 transition-all duration-300 hover-glow-purple"
          >
            <div className="w-16 h-16 mb-6">
              <AnimatedIcon icon={Target} color="#9C27B0" size={32} />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">{t('about.mission')}</h2>
            <p className="text-[#8A90A0] leading-relaxed">
              {t('about.missionText')}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="glass-effect rounded-2xl p-8 border border-yamai-cyan-500/30 hover:border-yamai-cyan-500 transition-all duration-300 hover-glow-cyan"
          >
            <div className="w-16 h-16 mb-6">
              <AnimatedIcon icon={Globe} color="#00BCD4" size={32} />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">{t('about.vision')}</h2>
            <p className="text-[#8A90A0] leading-relaxed">
              {t('about.visionText')}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="glass-effect rounded-2xl p-8 border border-yamai-cyan-500/30 hover:border-yamai-cyan-500 transition-all duration-300 hover-glow-cyan"
          >
            <div className="w-16 h-16 mb-6">
              <AnimatedIcon icon={Heart} color="#00BCD4" size={32} />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">{t('home.foundation.values')}</h2>
            <p className="text-[#8A90A0] leading-relaxed">
              {t('home.foundation.valuesText')}
            </p>
          </motion.div>
        </div>

        {/* Founding Team */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Founding Team</h2>
            <p className="text-[#8A90A0] text-lg">The minds behind Yamai</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-effect rounded-2xl p-8 border border-[#2B3544] hover:border-yamai-purple-500 transition-all duration-300 hover-glow-purple"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yamai-cyan-500 to-yamai-purple-500 flex items-center justify-center mb-4 text-white text-2xl font-bold">
                  {member.name.charAt(0)}
                </div>
                <h3 className="text-2xl font-semibold text-white mb-2">{member.name}</h3>
                <p className="text-yamai-cyan-400 font-medium mb-3">{member.role}</p>
                <p className="text-[#8A90A0] leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Future Team Divisions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Future Team Structure</h2>
            <p className="text-[#8A90A0] text-lg">Growing our capabilities across key domains</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {futureTeams.map((teamItem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-effect rounded-xl p-6 border border-[#2B3544] hover:border-yamai-cyan-500 transition-all duration-300 hover-glow-cyan"
              >
                <div className="w-12 h-12 mb-4">
                  <AnimatedIcon icon={teamItem.icon} color="#00BCD4" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{teamItem.name}</h3>
                <p className="text-[#8A90A0] text-sm leading-relaxed">{teamItem.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Commitments */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Sustainability */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect rounded-2xl p-8 border border-yamai-cyan-500/30 hover-glow-cyan"
          >
            <div className="flex items-center gap-3 mb-4">
              <AnimatedIcon icon={Leaf} color="#00BCD4" size={24} />
              <h2 className="text-2xl font-bold text-white">Sustainability</h2>
            </div>
            <p className="text-[#8A90A0] leading-relaxed mb-4">
              Yamai uses energy-efficient GPU clusters and lightweight models to reduce CO₂ emissions by 40%.
            </p>
            <p className="text-[#8A90A0] text-sm">
              Reference: NVIDIA Green AI 2024
            </p>
          </motion.div>

          {/* Ethics & Privacy */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect rounded-2xl p-8 border border-yamai-purple-500/30 hover-glow-purple"
          >
            <div className="flex items-center gap-3 mb-4">
              <AnimatedIcon icon={Award} color="#9C27B0" size={24} />
              <h2 className="text-2xl font-bold text-white">Ethics & Privacy</h2>
            </div>
            <p className="text-[#8A90A0] leading-relaxed mb-4">
              GDPR-compliant data handling, MDR-certified processes, transparent algorithms, and explainable decision-making at every step.
            </p>
            <p className="text-[#8A90A0] text-sm">
              Patient privacy and data security are non-negotiable.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About;